import styled from '@emotion/styled';
import React, {useCallback, useState} from 'react';
import {v4 as uuid} from 'uuid';

import {Snackbar, SnackbarType} from '@ELib/components/Snackbar';
import {useMountEffect} from '@ELib/utilities/hooks/effectHelpers';

// Re-export `SnackbarType` so consumers of `showSnackbar` do not need to import it separately
export {SnackbarType} from '@ELib/components/Snackbar';

type SnackbarWrapperProps = Omit<
  React.ComponentProps<typeof Snackbar>,
  'onClose'
> & {id: string; onClose: (arg0: string) => void};

type InternalMessage = {
  id: string;
  text: string;
  type: SnackbarType;
  timerId?: number;
  actions?: React.ReactElement[];
  stack?: string;
  overrideId?: string;
  counter: number;
};

/** SnackbarMessage is passed as object to showSnackbar */
type SnackbarMessage = {
  actions?: React.ReactElement[];
  id?: string;
  text: string;
  timeout?: number;
  /** Defaults to SUCCESS if not specified */
  type?: SnackbarType;
  /** Do not remove snackbar with timer */
  noSelfDismiss?: boolean;

  override?: {
    id: string;
    mode: 'REPLACE' | 'COUNTER';
  };
};

function SnackbarWrapper({
  onClose,
  id,
  ...props
}: SnackbarWrapperProps): React.ReactElement {
  const handleClose = useCallback(() => onClose(id), [onClose, id]);

  return <Snackbar onClose={handleClose} {...props} />;
}

let messages: InternalMessage[] = [];
let timers: number[] = [];
let rerenderSnackbar: (() => void) | undefined;

export const closeSnackbar = (messageId: string): void => {
  const foundItem = messages.find(({id}) => id === messageId);

  if (foundItem) {
    if (foundItem.timerId) {
      clearTimeout(foundItem.timerId);
      timers = timers.filter((timer) => timer !== foundItem.timerId);
    }

    messages = messages.filter((message) => messageId !== message.id);
    rerenderSnackbar?.();
  }
};

/** showSnackbar shows a snackbar with the specified text on the screen. Passed messages without type will default to SUCCESS messages. */
export function showSnackbar(message: SnackbarMessage): string {
  if (message.override) {
    const overrideId = message.override.id;
    const overrideMessageIndex = messages.findIndex(
      (msg) => msg.overrideId === overrideId
    );

    if (message.override.mode === 'COUNTER') {
      const msg = messages.at(overrideMessageIndex);
      if (msg) {
        msg.counter += 1;
        rerenderSnackbar?.();
        return msg.id;
      }
    }

    if (message.override.mode === 'REPLACE') {
      messages.splice(overrideMessageIndex, 1);
    }
  }

  // give the user time to read the message. it's very annoying if it disappears before you're done reading.
  const defaultTimeout = 20000;
  const id = message.id || uuid();
  const disallowSelfDismiss =
    message.noSelfDismiss !== undefined
      ? message.noSelfDismiss
      : message.type === SnackbarType.WARNING;

  if (messages.find(({id: id_}) => id === id_)) {
    throw new Error(`Duplicate message identifier: "${id}"`);
  }

  let timer;
  if (!disallowSelfDismiss) {
    timer = window.setTimeout(
      () => closeSnackbar(id),
      message.timeout || defaultTimeout
    );
    timers = [...timers, timer];
  }

  const internalMessage: InternalMessage = {
    actions: message.actions,
    id,
    stack: new Error().stack,
    text: message.text,
    timerId: timer,
    type: message.type || SnackbarType.SUCCESS,
    overrideId: message.override?.id,
    counter: 1,
  };

  messages = [...messages, internalMessage];
  rerenderSnackbar?.();

  return id;
}

export function useSnackbarContainer(): {snackbar: React.ReactElement} {
  const [, setLastUpdated] = useState<Date>();

  useMountEffect(() => {
    rerenderSnackbar = (): void => {
      setLastUpdated(new Date());
    };

    return (): void => {
      rerenderSnackbar = undefined;
      timers.forEach(clearTimeout);
    };
  });

  const removeMessage = useCallback((messageId: string) => {
    const foundItem = messages.find(({id}) => id === messageId);

    if (foundItem) {
      if (foundItem.timerId) {
        clearTimeout(foundItem.timerId);
        timers = timers.filter((timer) => timer !== foundItem.timerId);
      }

      messages = messages.filter((message) => messageId !== message.id);
      rerenderSnackbar?.();
    }
  }, []);

  return {
    snackbar: (
      <SnackbarContainer>
        {messages.map((message) => (
          <SnackbarWrapper
            key={message.id}
            counter={message.counter}
            id={message.id}
            message={message.text}
            snackbarType={message.type}
            onClose={removeMessage}
          >
            {message.actions}
          </SnackbarWrapper>
        ))}
      </SnackbarContainer>
    ),
  };
}

const SnackbarContainer = styled.div`
  padding: var(--gutter);
  position: absolute;
  display: flex;
  flex-direction: column-reverse;
  align-items: flex-end;
  pointer-events: none;
  gap: var(--gutter);
  overflow: visible;
  max-width: 100%;
  height: 100%;
  bottom: 0;
  right: 0;
  z-index: var(--zindex-snackbar);
`;
